import { useEffect, useState, useCallback } from "react";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { PageHeader } from "@/components/padroes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { contemBusca } from "@/lib/busca";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

interface AuditLogEntry {
  id: string;
  userId: string | null;
  userName: string;
  action: string;
  entity: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

interface AuditResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
}

const ACTION_LABELS: Record<string, string> = {
  AUTH_LOGIN: "Login",
  PERFIL_ATUALIZAR: "Perfil atualizado",
  NF_CRIAR: "NF enviada",
  NF_APROVAR: "NF aprovada",
  NF_RECUSAR: "NF recusada",
  NF_CANCELAR: "NF cancelada",
  NF_BAIXAR: "NF baixada",
  NF_BAIXAR_ZIP: "ZIP baixado",
  NF_RELATORIO_CSV: "Relatório exportado",
  USER_EXCLUIR: "Usuários excluídos",
  USER_VINCULAR_TOMADOR: "Tomador vinculado",
  WORKSPACE_IMPORTAR: "Workspace importado",
  TOMADOR_CRIAR: "Tomador criado",
  TOMADOR_EXCLUIR: "Tomador excluído",
  DEPARTAMENTO_CRIAR: "Departamento criado",
  DEPARTAMENTO_EXCLUIR: "Departamento excluído",
  INSTRUCAO_UPLOAD_CSV: "Competência lançada",
};

const ENTITY_LABELS: Record<string, string> = {
  NF: "Nota Fiscal",
  USER: "Usuário",
  TOMADOR: "Tomador",
  DEPARTAMENTO: "Departamento",
  FOLHA: "Folha",
  FOLHA_ITEM: "Item da folha",
  FOLHA_BENEFICIO: "Folha de benefícios",
  BENEFICIO_TIPO: "Benefício",
  EMPRESTIMO: "Empréstimo",
  BONIFICACAO: "Bonificação",
  BONIFICACAO_COMPETENCIA: "Parcela da bonificação",
  BONIFICACAO_ITEM: "Item da bonificação",
  CONFIG_BONIFICACAO: "Regras da bonificação",
  PARAMETRO: "Parâmetro",
};

/** Cor da ação pelo tipo (sufixo do código): aprovar verde, recusar/excluir vermelho etc. */
type TipoAcao = "aprovar" | "remover" | "criar" | "importar" | "login" | "atualizar";

const COR_ACAO: Record<TipoAcao, string> = {
  aprovar: "hsl(142 71% 34%)",
  remover: "hsl(0 75% 48%)",
  criar: "#2a78d6",
  importar: "#7c3aed",
  login: "hsl(210 15% 50%)",
  atualizar: "hsl(28 77% 45%)",
};

function tipoAcao(action: string): TipoAcao {
  if (action === "AUTH_LOGIN") return "login";
  if (/APROVAR/.test(action)) return "aprovar";
  if (/RECUSAR|EXCLUIR|CANCELAR/.test(action)) return "remover";
  if (/IMPORTAR|UPLOAD/.test(action)) return "importar";
  if (/CRIAR|LANCAR/.test(action)) return "criar";
  return "atualizar";
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function summarizeDetails(details: Record<string, unknown> | null): string {
  if (!details) return "—";
  const entries = Object.entries(details);
  if (entries.length === 0) return "—";
  return entries
    .slice(0, 3)
    .map(([k, v]) => {
      if (typeof v === "object" && v !== null) return `${k}: [...]`;
      return `${k}: ${String(v)}`;
    })
    .join(" • ");
}

/** Períodos rápidos; "personalizado" mostra os campos De / Até. */
type Periodo = "todos" | "hoje" | "7d" | "30d" | "90d" | "personalizado";

const PERIODOS: { value: Periodo; label: string }[] = [
  { value: "todos", label: "Todo o período" },
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "90d", label: "Últimos 90 dias" },
  { value: "personalizado", label: "Personalizado" },
];

/** AAAA-MM-DD no fuso local, `dias` atrás. */
const diaLocal = (dias = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DIAS_PERIODO: Partial<Record<Periodo, number>> = { hoje: 0, "7d": 6, "30d": 29, "90d": 89 };

const ALL_ACTIONS = Object.keys(ACTION_LABELS);
const ALL_ENTITIES = Object.keys(ENTITY_LABELS);
const LIMIT = 50;

const RHAuditoria = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [busca, setBusca] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterEntity, setFilterEntity] = useState("all");
  const [periodo, setPeriodo] = useState<Periodo>("todos");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (filterAction !== "all") params.set("action", filterAction);
      if (filterEntity !== "all") params.set("entity", filterEntity);
      if (filterFrom) params.set("from", filterFrom);
      if (filterTo) params.set("to", filterTo);

      const data = await api.get<AuditResponse>(`/audit?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
      setPage(data.page);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterEntity, filterFrom, filterTo]);

  useEffect(() => { load(1); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const trocarPeriodo = (p: Periodo) => {
    setPeriodo(p);
    if (p === "personalizado") return;
    const dias = DIAS_PERIODO[p];
    setFilterFrom(dias === undefined ? "" : diaLocal(dias));
    setFilterTo(dias === undefined ? "" : diaLocal());
  };

  const limpar = () => {
    setBusca("");
    setFilterAction("all");
    setFilterEntity("all");
    setPeriodo("todos");
    setFilterFrom("");
    setFilterTo("");
  };

  const temFiltro =
    !!busca || filterAction !== "all" || filterEntity !== "all" || periodo !== "todos";

  // A API não tem busca textual: a busca filtra a página carregada.
  const termo = busca.trim();
  const visiveis = termo
    ? logs.filter((log) =>
        contemBusca(
          [
            log.userName,
            ACTION_LABELS[log.action] ?? log.action,
            log.entity ? (ENTITY_LABELS[log.entity] ?? log.entity) : "",
            summarizeDetails(log.details),
            log.ip ?? "",
          ].join(" "),
          termo,
        ),
      )
    : logs;

  return (
    <RoleGuard role="rh">
      <AppLayout title="Auditoria">
        <div className="mx-auto flex max-w-7xl flex-col gap-4">
          <PageHeader
            titulo="Auditoria"
            subtitulo="Todas as ações feitas no sistema, com usuário, data e origem."
          />

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-[1_1_280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por usuário, entidade ou detalhe"
                className="h-[38px] bg-card pl-9"
                aria-label="Buscar na página"
              />
            </div>

            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="h-[38px] w-auto min-w-[170px] gap-2 bg-card" aria-label="Ação">
                <span className="text-muted-foreground">Ação</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ALL_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterEntity} onValueChange={setFilterEntity}>
              <SelectTrigger className="h-[38px] w-auto min-w-[170px] gap-2 bg-card" aria-label="Entidade">
                <span className="text-muted-foreground">Entidade</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ALL_ENTITIES.map((e) => (
                  <SelectItem key={e} value={e}>{ENTITY_LABELS[e]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={periodo} onValueChange={(v) => trocarPeriodo(v as Periodo)}>
              <SelectTrigger className="h-[38px] w-auto min-w-[200px] gap-2 bg-card" aria-label="Período">
                <span className="text-muted-foreground">Período</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODOS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {periodo === "personalizado" && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="h-[38px] w-[150px] bg-card"
                  aria-label="De"
                />
                <span className="text-sm text-muted-foreground">até</span>
                <Input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="h-[38px] w-[150px] bg-card"
                  aria-label="Até"
                />
              </div>
            )}

            {temFiltro && (
              <Button variant="ghost" onClick={limpar} className="text-muted-foreground">
                Limpar filtros
              </Button>
            )}
          </div>

          <section className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[1040px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[170px]">Data / Hora</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead className="w-[170px]">Ação</TableHead>
                  <TableHead className="w-[160px]">Entidade</TableHead>
                  <TableHead>Detalhes</TableHead>
                  <TableHead className="w-[130px]">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : visiveis.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  visiveis.map((log) => {
                    const cor = COR_ACAO[tipoAcao(log.action)];
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap py-[11px] text-muted-foreground">
                          {formatDateTime(log.createdAt)}
                        </TableCell>
                        <TableCell className="py-[11px] font-medium">{log.userName}</TableCell>
                        <TableCell className="py-[11px]">
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold" style={{ color: cor }}>
                            <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: cor }} />
                            {ACTION_LABELS[log.action] ?? log.action}
                          </span>
                        </TableCell>
                        <TableCell className="py-[11px]">
                          {log.entity ? (ENTITY_LABELS[log.entity] ?? log.entity) : "—"}
                          {log.entityId && (
                            <div className="max-w-[140px] truncate font-mono text-[10px] text-muted-foreground" title={log.entityId}>
                              {log.entityId}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[360px] truncate py-[11px] text-foreground/80" title={summarizeDetails(log.details)}>
                          {summarizeDetails(log.details)}
                        </TableCell>
                        <TableCell className="py-[11px] font-mono text-xs text-muted-foreground">
                          {log.ip ?? "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              {termo ? `${visiveis.length} de ${logs.length} nesta página · ` : ""}
              Total: {total} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1 || loading}
                onClick={() => load(page - 1)}
                aria-label="Página anterior"
              >
                <ChevronLeft />
              </Button>
              <span>Página {page} de {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages || loading}
                onClick={() => load(page + 1)}
                aria-label="Próxima página"
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHAuditoria;
